import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import { Alert, Loading } from '../../components';
import { Toggle, Tooltip } from '../../components/ui';
import useBreakpoint from '../../hooks/useBreakpoint';
import { MASection } from './MasterAdminSettings';
// The store's own team panel, reused rather than rebuilt: inviting someone to
// a store is the same job here as it is on Owner Admin.
import { TeamInvitePanel } from '../molecules/TeamInvitePanel';
import { storeState } from '../storeState';

import './entitlements-sections.scss';
import { ADDON_LABELS, COLUMN_LABELS, PLAN_OPTIONS } from '../planVocab';
import { FEATURE_GUIDE } from '../featureGuide';
import FeatureScreen from '../featurePreview/FeatureScreen';

/**
 * Master admin governance → plan/add-on entitlements (SaaS launch, Focus 2).
 *
 * Two sections:
 *  - PlanAddonsSection    per-store card: plan, add-on toggles, limit
 *                         overrides with live usage meters, feature overrides.
 *  - FeatureMatrixSection the editable "which feature is in which plan" grid.
 *
 * Everything auto-saves on change (house rule: no Save buttons) with a quiet
 * "Saving… / All changes saved ✓" status per section.
 */

/**
 * Commercial names. The plan SLUGS are frozen (they are written into
 * stores.plan / feature_quotas.plan on every environment) — CORE/PLUS/PRO are
 * labels only, so free/black/custom still appear in payloads. The matrix
 * section reads its labels off the API (`column_meta`); these are the fallback
 * and the source for the per-store card, which has no label payload.
 * Backend source of truth: App\Constants\Features::LABELS.
 */

const IPlan = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.5 6H14.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M4 9.5H8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M4 11.5H6.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const IGrid = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="1.5" width="5.2" height="5.2" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <rect x="9.3" y="1.5" width="5.2" height="5.2" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <rect x="1.5" y="9.3" width="5.2" height="5.2" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <rect x="9.3" y="9.3" width="5.2" height="5.2" rx="1" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

function SaveStatus({ state }) {
  if (state === 'saving') return <span className="ent-save ent-save--saving">Saving…</span>;
  if (state === 'saved') return <span className="ent-save ent-save--saved">All changes saved ✓</span>;
  return null;
}

const isAddonColumn = (column) => column.startsWith('addon:');

/**
 * A matrix cell: custom check chip (the native checkbox reads as unstyled on
 * the dark theme and is hard to track across six columns).
 */
function CheckCell({ checked, label, onChange }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      className={`ent-check ${checked ? 'ent-check--on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

function limitText(max, used) {
  if (max === null || max === undefined || max === -1) return `${used ?? 0} used · unlimited`;
  return `${used ?? 0} of ${max} used`;
}

// ─────────────────────────────────────────────────────────────────────────────

export function PlanAddonsSection() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  // A selected store that is not an account yet. It has no plan of its own,
  // so the ordinary card does not apply to it — the give-access panel does.
  const [pending, setPending] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const savedTimer = useRef(null);

  const loadSeq = useRef(0);

  const load = async (query = '') => {
    // Search-as-you-type fires a request per keystroke-ish, and they do not
    // come back in order — a slower early one landing last would replace the
    // results for what was actually typed. Only the newest answer counts.
    const seq = ++loadSeq.current;

    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/admin/entitlements/stores', { params: query ? { q: query } : {} });
      if (seq !== loadSeq.current) return;
      setItems(data.items || []);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError('Could not load stores.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  // NO separate load() on mount. The debounced search effect below already
  // runs on mount with an empty query, and having both meant two requests in
  // flight at once: the unfiltered one is slower, landed last, and overwrote
  // the filtered result — so arriving with ?store=2043 showed the search box
  // filled in and the unfiltered list beneath it (Alex, 2026-10-26).
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  // Arrived from the Stores console with a store in hand (?store=3725):
  // search for it so the admin lands on the row they were sent for rather
  // than on a list they have to search again.
  useEffect(() => {
    const asked = new URLSearchParams(window.location.search).get('store');
    if (asked) setQ(asked);
  }, []);

  // Search as you type. The backend only looks beyond the accounts when it is
  // given a query, so this is also how a store with no plan is found at all.
  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const openStore = async (store) => {
    const id = store.id;
    setSelectedId(id);
    setError('');

    // Not an account: opening its detail would silently return its PARENT's
    // card, because the endpoint resolves a substore to the account above it.
    if (store.is_subscriber === false) {
      setPending(store);
      setDetail(null);
      return;
    }

    setPending(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`/api/admin/entitlements/stores/${id}`);
      setDetail(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load this store.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  /** Make a store an account and set it going, in one request. */
  const grant = async (store, payload) => {
    setSaveState('saving');
    setError('');
    try {
      const { data } = await axios.put(`/api/admin/entitlements/stores/${store.id}`, {
        make_account: true,
        ...payload,
      });
      setPending(null);
      setDetail(data);
      setSaveState('saved');
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaveState('idle'), 2500);
      load(q.trim());
    } catch (e) {
      setSaveState('idle');
      setError(e?.response?.data?.message || 'Could not give this store access.');
    }
  };

  /** Auto-save a partial payload; the fresh detail comes back in the response. */
  const save = async (payload) => {
    if (!detail) return;
    setSaveState('saving');
    setError('');
    try {
      const { data } = await axios.put(`/api/admin/entitlements/stores/${detail.id}`, payload);
      setDetail(data);
      setItems((prev) => prev.map((s) => (
        s.id === data.id ? { ...s, plan: data.plan, addons: data.addons } : s
      )));
      setSaveState('saved');
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaveState('idle'), 2500);
    } catch (e) {
      setSaveState('idle');
      setError(e?.response?.data?.message || 'Failed to save — the change was not applied.');
    }
  };

  const toggleAddon = (addon) => {
    const current = detail?.addons || [];
    const next = current.includes(addon) ? current.filter((a) => a !== addon) : [...current, addon];
    save({ addons: next });
  };

  const overrideFor = (feature) =>
    (detail?.overrides || []).find((o) => o.feature === feature);

  const accounts = items.filter((s) => s.is_subscriber !== false);
  const notAccounts = items.filter((s) => s.is_subscriber === false);

  const storeLabel = (store) => store.display_name || store.name || `Store ${store.id}`;

  const row = (store) => (
    <button
      key={store.id}
      type="button"
      aria-current={selectedId === store.id ? 'true' : undefined}
      className={`ent-store-row ${selectedId === store.id ? 'ent-store-row--active' : ''}`}
      onClick={() => openStore(store)}
    >
      <span className="ent-store-row__main">
        <span className="ent-store-row__name">{storeLabel(store)}</span>
        <span className="ent-store-row__meta">
          #{store.id}
          {store.is_default_store && ' · default store'}
          {/* "not an account yet" and "has no plan" are different facts, and
              the meta line was using the first to say the second. The chip
              on the right says what the store IS; this says what it is not
              yet attached to. */}
          {store.is_subscriber === false && ' · not an account yet'}
          {store.is_subscriber !== false && ` · ${(store.addons || []).length || 'no'} add-ons`}
        </span>
      </span>
      {store.billing_active === false && (
        <span className="ent-plan-chip ent-plan-chip--suspended">Suspended</span>
      )}
      {/* A store with no plan RESOLVES to CORE, because CORE is the floor —
          but labelling the row CORE beside "no plan yet" says two things at
          once. The commercial state comes from one shared definition
          (Store::commercialState) so this row, the Stores console and the
          chat section cannot disagree about what Beta means. */}
      {storeState(store).key === 'subscribed' ? (
        <span className={`ent-plan-chip ent-plan-chip--${store.plan}`}>
          {COLUMN_LABELS[store.plan] || store.plan}
        </span>
      ) : (
        <span className={`ent-plan-chip ent-plan-chip--state-${storeState(store).tone}`}>
          {storeState(store).label}
        </span>
      )}
    </button>
  );

  return (
    <MASection
      title="Plans & Add-ons"
      icon={<IPlan />}
      /* Addressable as ?section=plans-addons, so the Stores console can hand
         a store straight over when it is ready to start paying — the two
         halves of onboarding a subscriber were otherwise two screens with
         nothing joining them. */
      anchor="plans-addons"
      headerExtra={<SaveStatus state={saveState} />}
    >
      <div className="ent-pa">

        <div className="ent-pa__head">
          <p className="ent-desc">What each store has bought, and what it is allowed to use.</p>
          {detail?.mode && detail.mode !== 'enforce' && (
            <span className="ent-mode-badge">
              <span className="ent-mode-badge__dot" aria-hidden="true" />
              {detail.mode === 'shadow'
                ? 'Enforcement in shadow mode — logging only'
                : 'Enforcement off platform-wide'}
            </span>
          )}
        </div>

        <UpgradeRequestsQueue onActioned={(storeId) => storeId && openStore({ id: storeId })} />

        <div className="ent-pa__cols">

          <div className="ent-pa__list">
            <label className="ent-pa__search">
              <span className="ent-pa__search-label">Search stores by name or id</span>
              <input
                type="search"
                value={q}
                placeholder="Search stores by name or id"
                onChange={(e) => setQ(e.target.value)}
              />
            </label>

            {loading ? <Loading /> : (
              <div className="ent-store-list">
                {items.length === 0 && (
                  <p className="ent-pa__empty">
                    {q.trim()
                      ? 'No store matches that name or id.'
                      : 'No accounts yet. Search for a store to give it access.'}
                  </p>
                )}

                {accounts.map(row)}

                {notAccounts.length > 0 && (
                  <>
                    {/* These are stores that are not ACCOUNTS yet, which is a
                        different thing from having no plan — the chip on each
                        row says which. */}
                    <p className="ent-store-list__group">Not an account yet</p>
                    {notAccounts.map(row)}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="ent-pa__card">
            {pending && (
              <GiveAccessPanel
                store={pending}
                busy={saveState === 'saving'}
                onGrant={(payload) => grant(pending, payload)}
              />
            )}

            {detailLoading && <Loading />}

            {detail && !detailLoading && !pending && (
              <>
                <div className="ent-card-head">
                  <span className="ent-card-head__name">
                    {detail.display_name || detail.name || `Store ${detail.id}`}
                    <span className="ent-card-head__id">#{detail.id}</span>
                  </span>
                  <SubscriptionStatus detail={detail} />
                  {detail.is_exempt && (
                    <span className="ent-exempt">never gated (platform store)</span>
                  )}
                </div>

                <section className="ent-block">
                  <h4 className="ent-block__title">Plan</h4>
                  <div className="ent-plan-picker" role="radiogroup" aria-label="Plan">
                    {PLAN_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        /* The ASSIGNED plan, not the resolved one — on a lapsed or
                           trialling store they differ, and the picker must show what
                           was set, or an admin "fixing" it would pick the value it
                           already has. The status line above explains the gap. */
                        className={`ent-plan-picker__option ${(detail.plan_stored ?? detail.plan) === option.value ? 'ent-plan-picker__option--active' : ''}`}
                        onClick={() => (detail.plan_stored ?? detail.plan) !== option.value && save({ plan: option.value })}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="ent-block">
                  <h4 className="ent-block__title">
                    Add-ons
                    <span className="ent-block__count">
                      {(detail.addons || []).length} of {Object.keys(ADDON_LABELS).length} on
                    </span>
                  </h4>
                  <div className="ent-addon-grid">
                    {Object.keys(ADDON_LABELS).map((addon) => {
                      const on = (detail.addons || []).includes(addon);

                      return (
                        <button
                          key={addon}
                          type="button"
                          aria-pressed={on}
                          className={`ent-addon ${on ? 'ent-addon--on' : ''}`}
                          onClick={() => toggleAddon(addon)}
                        >
                          <span className="ent-addon__mark" aria-hidden="true">
                            {on && (
                              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                                <path d="M2.5 7.5l3 3 6-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </span>
                          {ADDON_LABELS[addon]}
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="ent-block">
                  <h4 className="ent-block__title">Dates &amp; access</h4>
                  <SubscriptionPeriodRows detail={detail} onSave={save} />
                </section>

                <section className="ent-block">
                  <h4 className="ent-block__title">Limits for this store</h4>
                  <LimitRow
                    label="Monthly listings"
                    feature="limit_listings"
                    usage={detail.usage?.limit_listings}
                    override={overrideFor('limit_listings')}
                    onSave={(value) => save({ overrides: { limit_listings: value } })}
                  />
                  <LimitRow
                    label="Admin seats"
                    feature="limit_seats"
                    usage={detail.usage?.limit_seats}
                    override={overrideFor('limit_seats')}
                    onSave={(value) => save({ overrides: { limit_seats: value } })}
                  />
                </section>

                <section className="ent-block">
                  <h4 className="ent-block__title">Exceptions</h4>
                  <OverridesEditor detail={detail} onSave={save} />
                </section>

                <section className="ent-block">
                  <h4 className="ent-block__title">Members</h4>
                  <TeamInvitePanel storeId={detail.id} storeName={detail.display_name || detail.name} />
                </section>
              </>
            )}

            {!detail && !pending && !detailLoading && (
              <p className="ent-pa__empty">Pick a store on the left.</p>
            )}
          </div>
        </div>
      </div>

      {error && <Alert type="error" message={error} />}
    </MASection>
  );
}

/**
 * A store that is not an account yet.
 *
 * There are exactly two things it can be, and the screen now says so rather
 * than offering three buttons whose difference you had to infer (Alex,
 * 2026-10-26):
 *
 *   BETA       — no plan, nobody paying, everything works. This is what a
 *                new store already IS, so it needs no action at all.
 *   SUBSCRIBER — a plan. Which plan, and whether it starts as a free trial
 *                or as paid, are two separate choices and are asked
 *                separately.
 *
 * Making it a subscriber is also what makes it an account in its own right:
 * its own plan, add-ons and limits instead of inheriting them.
 */
function GiveAccessPanel({ store, busy, onGrant }) {
  const [plan, setPlan] = useState('black');
  // 'trial' = 30 days free, then it lapses to CORE unless renewed.
  // 'paid'  = paying from today; an expiry can be set afterwards.
  // 'comped'= on the house, indefinitely, ignoring every date.
  const [start, setStart] = useState('trial');

  const in30Days = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  };

  const submit = () => {
    if (start === 'trial') return onGrant({ plan, trial_ends_at: in30Days() });
    if (start === 'comped') return onGrant({ plan, comped: true });
    return onGrant({ plan });
  };

  return (
    <div className="ent-grant">
      <div className="ent-card-head">
        <span className="ent-card-head__name">
          {store.display_name || store.name || `Store ${store.id}`}
          <span className="ent-card-head__id">#{store.id}</span>
        </span>
        <span className="ent-plan-chip ent-plan-chip--state-muted">No plan</span>
      </div>

      <div className="ent-grant__block">
        <span className="ent-grant__label">Plan</span>
        <div className="ent-plan-picker" role="radiogroup" aria-label="Plan">
          {PLAN_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={plan === option.value}
              className={`ent-plan-picker__option ${plan === option.value ? 'ent-plan-picker__option--active' : ''}`}
              onClick={() => setPlan(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ent-grant__block">
        <span className="ent-grant__label">How it starts</span>
        <div className="ent-plan-picker" role="radiogroup" aria-label="How the plan starts">
          {[
            // The first and third make the store BETA — a plan nobody is
            // paying for. The middle one makes it a subscriber.
            { value: 'trial', label: '30-day free trial' },
            { value: 'paid', label: 'Paid from today' },
            { value: 'comped', label: 'Free of charge' },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={start === option.value}
              className={`ent-plan-picker__option ${start === option.value ? 'ent-plan-picker__option--active' : ''}`}
              onClick={() => setStart(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="ent-grant__foot">
          {start === 'trial' && 'Counts as Beta. Full plan for 30 days, then back to CORE unless you set an expiry or mark it paid.'}
          {start === 'paid' && 'Counts as Subscribed. On the plan from today, with no expiry until you set one.'}
          {start === 'comped' && 'Counts as Beta. On the plan indefinitely, ignoring both dates — for partners and internal stores.'}
        </p>
      </div>

      <div className="ent-grant__actions">
        <button type="button" className="ent-grant__primary" disabled={busy} onClick={submit}>
          {busy ? 'Working…' : `Make this store a ${PLAN_OPTIONS.find((o) => o.value === plan)?.label} subscriber`}
        </button>
      </div>

      <p className="ent-grant__foot">
        You can change the plan, the dates and the add-ons straight afterwards, and
        put the store&apos;s people on it from the Stores section.
      </p>
    </div>
  );
}

// ── Subscription period: assign a bundle by hand, with an expiry ───────────
// Until Stripe (Focus 7) writes these dates from a real subscription, a master
// admin sets them here. Stripe will later write the SAME two fields, so this
// is the seam rather than a throwaway.

const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : '');

/** Whole days from today to `iso`; negative when it is in the past. */
const daysUntil = (iso) => {
  if (!iso) return null;
  const then = new Date(String(iso).slice(0, 10));
  const today = new Date(new Date().toISOString().slice(0, 10));
  return Math.round((then - today) / 86400000);
};

const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

/**
 * One line that answers "what is this store on, and until when?" — the thing
 * an admin actually wants to know before touching anything. Deliberately one
 * sentence: the detail lives in the controls below it.
 */
function SubscriptionStatus({ detail }) {
  const planLabel = COLUMN_LABELS[detail.plan] || detail.plan || '—';
  const storedLabel = COLUMN_LABELS[detail.plan_stored] || detail.plan_stored;

  let tone = 'ok';
  let text;

  if (detail.billing_active === false) {
    // The most severe state, so it wins the line — a suspended account is
    // blocked outright, whatever its plan says.
    tone = 'lapsed';
    text = 'SUSPENDED · all users on this account are blocked from the app';
  } else if (detail.is_exempt) {
    tone = 'muted';
    text = `${planLabel} · platform store, never gated`;
  } else if (detail.comped) {
    tone = 'comped';
    text = `${planLabel} · comped — no expiry, ignores payment`;
  } else if (detail.on_trial) {
    const left = daysUntil(detail.trial_ends_at);
    tone = 'trial';
    text = `${planLabel} on trial · ${left} day${left === 1 ? '' : 's'} left, then CORE (${formatDay(detail.trial_ends_at)})`;
  } else if (detail.plan_lapsed) {
    const ago = Math.abs(daysUntil(detail.plan_ends_at));
    tone = 'lapsed';
    text = `Lapsed ${ago} day${ago === 1 ? '' : 's'} ago — running as CORE${storedLabel ? ` (was ${storedLabel})` : ''}`;
  } else if (detail.plan_ends_at) {
    const left = daysUntil(detail.plan_ends_at);
    text = `${planLabel} · expires in ${left} day${left === 1 ? '' : 's'} (${formatDay(detail.plan_ends_at)})`;
  } else {
    text = `${planLabel} · no expiry set`;
  }

  return <p className={`ent-status ent-status--${tone}`}>{text}</p>;
}

/**
 * The two dates. Each is a plain date field plus the shortcuts that cover
 * ~every real case, so assigning a bundle is one or two clicks rather than
 * arithmetic. Saving is immediate, like everything else on this card.
 */
function SubscriptionPeriodRows({ detail, onSave }) {
  if (detail.is_exempt) return null;

  const trialActive = Boolean(detail.on_trial);

  return (
    <>
      <div className="ent-detail__row">
        <span className="ent-detail__label">
          Expires
          <span className="ent-detail__meter">
            {detail.comped
              ? 'ignored while comped'
              : detail.plan_ends_at
                ? `paid access ends ${formatDay(detail.plan_ends_at)}`
                : 'no expiry — access continues until changed'}
          </span>
        </span>
        <div className="ent-period">
          <input
            type="date"
            value={toDateInput(detail.plan_ends_at)}
            aria-label="Plan expiry date"
            onChange={(e) => onSave({ plan_ends_at: e.target.value || null })}
          />
          <button type="button" className="rc-btn rc-btn--small" onClick={() => onSave({ plan_ends_at: plusDays(30) })}>
            +30 days
          </button>
          <button type="button" className="rc-btn rc-btn--small" onClick={() => onSave({ plan_ends_at: plusDays(365) })}>
            +1 year
          </button>
          {detail.plan_ends_at && (
            <button type="button" className="rc-btn rc-btn--small" onClick={() => onSave({ plan_ends_at: null })}>
              Never
            </button>
          )}
        </div>
      </div>

      <div className="ent-detail__row">
        <span className="ent-detail__label">
          Free trial
          <span className="ent-detail__meter">
            {trialActive
              ? `running as PLUS until ${formatDay(detail.trial_ends_at)}`
              : 'CORE store + trial = PLUS until the date passes'}
          </span>
        </span>
        <div className="ent-period">
          <input
            type="date"
            value={toDateInput(detail.trial_ends_at)}
            aria-label="Trial end date"
            onChange={(e) => onSave({ trial_ends_at: e.target.value || null })}
          />
          <button
            type="button"
            className="rc-btn rc-btn--small"
            title="Set the plan to CORE and give 30 days of PLUS"
            onClick={() => onSave({ plan: 'free', trial_ends_at: plusDays(30) })}
          >
            Start 30-day trial
          </button>
          {detail.trial_ends_at && (
            <button type="button" className="rc-btn rc-btn--small" onClick={() => onSave({ trial_ends_at: null })}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="ent-detail__row">
        <span className="ent-detail__label">
          Comped
          <span className="ent-detail__meter">
            keeps this plan regardless of payment — ignores both dates
          </span>
        </span>
        <Toggle checked={Boolean(detail.comped)} onChange={() => onSave({ comped: !detail.comped })} />
      </div>

      {/* Absorbed from the retired Store Access section: suspension is an
          account state, so it belongs beside the plan rather than on its own
          screen. Expiry downgrades to CORE; this cuts access off entirely. */}
      <div className="ent-detail__row">
        <span className="ent-detail__label">
          Account access
          <span className="ent-detail__meter">
            {detail.is_default_store
              ? 'the default store can never be suspended'
              : detail.billing_active === false
                ? 'SUSPENDED — this account’s users are blocked from the app'
                : 'active — suspending blocks every user on this account'}
          </span>
        </span>
        <div className="ent-plan-picker" role="radiogroup" aria-label="Account access">
          <button
            type="button"
            disabled={detail.is_default_store}
            className={`ent-plan-picker__option ${detail.billing_active !== false ? 'ent-plan-picker__option--active' : ''}`}
            onClick={() => detail.billing_active === false && onSave({ billing_active: true })}
          >
            Active
          </button>
          <button
            type="button"
            disabled={detail.is_default_store}
            className={`ent-plan-picker__option ${detail.billing_active === false ? 'ent-plan-picker__option--danger' : ''}`}
            onClick={() => detail.billing_active !== false && onSave({ billing_active: false })}
          >
            Suspended
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Pending in-app upgrade requests ("Request upgrade" buttons on limit meters
 * and locked screens land here). Actioning is bookkeeping — the actual plan
 * change happens on the store's card, so "Open store" jumps straight to it.
 */
function UpgradeRequestsQueue({ onActioned }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get('/api/admin/entitlements/upgrade-requests', { params: { status: 'pending' } });
      setItems(data.items || []);
    } catch (e) {
      setError('Could not load upgrade requests.');
    }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (id, status) => {
    setError('');
    try {
      await axios.patch(`/api/admin/entitlements/upgrade-requests/${id}`, { status });
      setItems((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update the request.');
    }
  };

  if (!items.length && !error) return null;

  return (
    <div className="ent-upreqs">
      <span className="ent-detail__label">
        Upgrade requests
        <span className="ent-detail__meter">{items.length} pending</span>
      </span>
      {items.map((r) => (
        <div className="ent-upreqs__row" key={r.id}>
          <span className="ent-upreqs__who">
            <strong>{r.store_name || `Store ${r.store_id}`}</strong>
            {r.requested_by ? ` — ${r.requested_by}` : ''}
            <span className="ent-upreqs__meta">
              {r.feature ? `wants: ${r.feature.replace(/_/g, ' ')}` : 'general upgrade'}
              {' · '}
              {new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
            </span>
          </span>
          <button type="button" className="rc-btn rc-btn--small" onClick={() => onActioned(r.store_id)}>
            Open store
          </button>
          <button type="button" className="rc-btn rc-btn--small" onClick={() => resolve(r.id, 'actioned')}>
            Actioned
          </button>
          <button type="button" className="rc-btn rc-btn--small" onClick={() => resolve(r.id, 'dismissed')}>
            Dismiss
          </button>
        </div>
      ))}
      {error && <Alert type="error" message={error} />}
    </div>
  );
}

/**
 * One limit line: live usage meter + an editable per-store override.
 * The number input auto-saves on blur/Enter (typing every digit shouldn't
 * fire a request); the unlimited/clear actions save immediately.
 */
function LimitRow({ label, usage, override, onSave }) {
  const [draft, setDraft] = useState('');
  const effectiveMax = usage?.max;

  useEffect(() => {
    setDraft(override && override.value >= 0 ? String(override.value) : '');
  }, [override?.value]);

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (trimmed === '') return;
    const value = Math.max(0, parseInt(trimmed, 10) || 0);
    if (override && override.value === value) return;
    onSave(value);
  };

  return (
    <div className="ent-detail__row">
      <span className="ent-detail__label">
        {label}
        <span className="ent-detail__meter">{limitText(effectiveMax, usage?.used)}</span>
      </span>
      <div className="ent-limit-controls">
        <input
          type="number"
          min="0"
          value={draft}
          placeholder={effectiveMax === null || effectiveMax === undefined ? '∞' : String(effectiveMax)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
          aria-label={`${label} override`}
        />
        <button
          type="button"
          className={`rc-btn rc-btn--small ${override?.value === -1 ? 'rc-btn--active' : ''}`}
          title="Unlimited for this store"
          onClick={() => onSave(-1)}
        >
          ∞
        </button>
        {override && (
          <button
            type="button"
            className="rc-btn rc-btn--small"
            title="Remove the override — fall back to the plan value"
            onClick={() => onSave(null)}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

/** Per-feature overrides beyond the two limits — the Custom plan's engine. */
function OverridesEditor({ detail, onSave }) {
  const [feature, setFeature] = useState('');

  // Core features are excluded: they are always on for everyone, so an
  // override would be a no-op the backend ignores anyway.
  const toggles = useMemo(
    () => (detail.catalog || []).filter((f) => f.type === 'toggle' && f.state !== 'core'),
    [detail.catalog],
  );

  const rows = (detail.overrides || []).filter(
    (o) => o.feature !== 'limit_listings' && o.feature !== 'limit_seats',
  );

  const labelOf = (key) => (detail.catalog || []).find((f) => f.key === key)?.label || key;
  const effectiveOf = (key) => detail.effective?.[key];

  return (
    <div className="ent-overrides">
      <span className="ent-detail__label">Feature overrides</span>
      {rows.length === 0 && <p className="ent-overrides__empty">None — this store follows its plan and add-ons exactly.</p>}
      {rows.map((o) => (
        <div className="ent-overrides__row" key={o.feature}>
          <span className="ent-overrides__name">{labelOf(o.feature)}</span>
          <span className={`ent-overrides__value ${o.value === 0 ? 'ent-overrides__value--off' : ''}`}>
            {o.value === 0 ? 'Forced OFF' : 'Forced ON'}
          </span>
          <button type="button" className="rc-btn rc-btn--small" onClick={() => onSave({ overrides: { [o.feature]: null } })}>
            Remove
          </button>
        </div>
      ))}

      <div className="ent-overrides__add">
        <select value={feature} onChange={(e) => setFeature(e.target.value)} aria-label="Feature to override">
          <option value="">Add an override…</option>
          {toggles.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
        {feature && (
          <>
            <button
              type="button"
              className="rc-btn rc-btn--small"
              onClick={() => { onSave({ overrides: { [feature]: 1 } }); setFeature(''); }}
            >
              Force ON
            </button>
            <button
              type="button"
              className="rc-btn rc-btn--small"
              onClick={() => { onSave({ overrides: { [feature]: 0 } }); setFeature(''); }}
            >
              Force OFF
            </button>
            <span className="ent-overrides__hint">
              currently {effectiveOf(feature) === 0 ? 'off' : 'on'} via plan/add-ons
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The editable "which feature belongs to which plan or add-on" grid.
 *
 * Two jobs, and the UI is built around the second one being the common case:
 *   1. read the allocation at a glance;
 *   2. MOVE a feature to a different plan or add-on.
 *
 * On the wide grid the checkboxes ARE the control (Alex, 2026-10-28: the
 * "Sold with" dropdown left the desktop table); ticking a plan keeps the
 * CORE ⊂ PLUS ⊂ PRO ladder whole. Its column became "Review": a free-text
 * note per row on what still needs review or work, auto-saved, gating nothing.
 * Hovering a feature name shows what it is, where it lives, and a sketch of
 * the control (settings/featureGuide.js).
 *
 * Below 900px (the same line as every other table→cards switch in the app)
 * each feature becomes a card, and there "Sold with" stays — nine columns of
 * checkboxes do not survive a phone.
 */
export function FeatureMatrixSection() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [query, setQuery] = useState('');
  // 'live' | 'future' | 'all'. Replaces the old "Show Future Release"
  // checkbox: three states, one control, and the default lands on the rows
  // that actually gate something.
  const [stateFilter, setStateFilter] = useState('live');
  // Which card's "Sold with" picker is open (phone layout). One at a time.
  const [openKey, setOpenKey] = useState(null);
  const savedTimer = useRef(null);
  const debounceTimers = useRef({});
  const { isAtLeastMd } = useBreakpoint();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/admin/entitlements/matrix');
      setMatrix(data);
    } catch (e) {
      setError('Could not load the feature matrix.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => () => {
    clearTimeout(savedTimer.current);
    Object.values(debounceTimers.current).forEach(clearTimeout);
  }, []);

  const put = async (payload) => {
    setSaveState('saving');
    setError('');
    try {
      await axios.put('/api/admin/entitlements/matrix', payload);
      setSaveState('saved');
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaveState('idle'), 2500);
    } catch (e) {
      setSaveState('idle');
      setError(e?.response?.data?.message || 'Failed to save — reload before editing further.');
    }
  };

  /** Optimistic local update + save (debounced for typed numbers). */
  const setCell = (feature, column, value, { debounce = false } = {}) => {
    setMatrix((prev) => ({
      ...prev,
      features: prev.features.map((f) => (
        f.key === feature ? { ...f, values: { ...f.values, [column]: value } } : f
      )),
    }));

    const timerKey = `${feature}:${column}`;
    clearTimeout(debounceTimers.current[timerKey]);
    const send = () => put({ values: { [feature]: { [column]: value } } });
    if (debounce) debounceTimers.current[timerKey] = setTimeout(send, 600);
    else send();
  };

  /** Several columns of one row in a single request. */
  const setCells = (featureKey, patch) => {
    setMatrix((prev) => ({
      ...prev,
      features: prev.features.map((f) => (
        f.key === featureKey ? { ...f, values: { ...f.values, ...patch } } : f
      )),
    }));
    put({ values: { [featureKey]: patch } });
  };

  /**
   * The row's "needs review / work" note — typed, so debounced like a limit.
   * `flush` (on blur) sends a pending edit at once, so leaving the page right
   * after typing does not drop the last few keystrokes.
   */
  const setNote = (featureKey, note, { flush = false } = {}) => {
    const timerKey = `${featureKey}:note`;
    const send = () => {
      delete debounceTimers.current[timerKey];
      put({ notes: { [featureKey]: note } });
    };

    if (flush) {
      if (!debounceTimers.current[timerKey]) return;
      clearTimeout(debounceTimers.current[timerKey]);
      send();
      return;
    }

    setMatrix((prev) => ({
      ...prev,
      features: prev.features.map((f) => (f.key === featureKey ? { ...f, review_note: note } : f)),
    }));
    clearTimeout(debounceTimers.current[timerKey]);
    debounceTimers.current[timerKey] = setTimeout(send, 700);
  };

  /**
   * Switch one plan or add-on on or off for a feature — from the picker or
   * from the cell itself, which are the same selection. The plan ladder is
   * kept whole by cellsForToggle, so one click can write three cells.
   */
  const toggleColumn = (feature, column, next) => {
    setCells(feature.key, cellsForToggle(feature, column, next, columns));
  };

  const columns = matrix?.column_meta
    || (matrix?.columns || []).map((key) => ({
      key,
      label: COLUMN_LABELS[key] || key,
      group: isAddonColumn(key) ? 'addon' : 'plan',
    }));

  /** Category → rows, after the search box and the Future Release filter. */
  const grouped = useMemo(() => {
    if (!matrix) return [];
    const needle = query.trim().toLowerCase();
    const byCategory = new Map();

    matrix.features.forEach((f) => {
      const isFuture = f.state === 'future';
      if (stateFilter === 'live' && isFuture) return;
      if (stateFilter === 'future' && !isFuture) return;
      if (needle && !f.label.toLowerCase().includes(needle) && !f.key.includes(needle)) return;
      if (!byCategory.has(f.category)) byCategory.set(f.category, []);
      byCategory.get(f.category).push(f);
    });

    return Array.from(byCategory.entries());
  }, [matrix, query, stateFilter]);

  const visibleCount = grouped.reduce((n, [, rows]) => n + rows.length, 0);

  return (
    <MASection title="Feature Matrix" icon={<IGrid />} headerExtra={<SaveStatus state={saveState} />}>
      <p className="ent-desc">
        Pick the plans and add-ons each feature is sold with. Applies to every store at once.
      </p>

      {loading ? <Loading /> : matrix && (
        <>
          <div className="ent-tools">
            <input
              type="search"
              className="ent-tools__search"
              value={query}
              placeholder="Filter features…"
              aria-label="Filter features"
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="ent-seg" role="group" aria-label="Which features to show">
              {[
                { key: 'live', label: 'Live' },
                { key: 'future', label: 'Future' },
                { key: 'all', label: 'All' },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`ent-seg__btn ${stateFilter === option.key ? 'ent-seg__btn--on' : ''}`}
                  aria-pressed={stateFilter === option.key}
                  onClick={() => setStateFilter(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <span className="ent-tools__count">
              {visibleCount} of {matrix.features.length} features
            </span>
          </div>

          {visibleCount === 0 ? (
            <p className="ent-tools__empty">No features match that filter.</p>
          ) : isAtLeastMd ? (
            <MatrixTable
              grouped={grouped}
              columns={columns}
              onCell={setCell}
              onToggle={toggleColumn}
              onNote={setNote}
            />
          ) : (
            <MatrixCards
              grouped={grouped}
              columns={columns}
              openKey={openKey}
              onOpenKey={setOpenKey}
              onCell={setCell}
              onToggle={toggleColumn}
              onNote={setNote}
            />
          )}
        </>
      )}

      {error && <Alert type="error" message={error} />}
    </MASection>
  );
}

/* ── "Sold with" ──────────────────────────────────────────────────────────

   "Sold with" and the nine columns are ONE selection shown two ways (Alex,
   2026-10-26). They used to be two models on the same row: you picked a single
   tier, the ticks were DERIVED from it, and a hand-ticked cell was an
   "exception". A row could then read "sold with E-Commerce Manager" while PLUS
   and PRO were ticked — correct, because that add-on is bundled into PLUS and
   above, but nothing on screen said so, and it read as a contradiction. The
   chips are now simply the ticked columns, so the two controls cannot
   disagree and there is no such thing as an exception.

   Phone cards only since 2026-10-28 — the desktop grid edits the checkboxes
   directly (Alex). */

/** The columns this feature is currently sold in. */
function soldColumns(feature, columns) {
  return columns
    .filter((c) => (feature.type === 'limit' ? c.group !== 'addon' : true))
    .filter((c) => (feature.values[c.key] ?? 0) !== 0)
    .map((c) => c.key);
}

/**
 * The cells to write when one column is toggled.
 *
 * Plans are a ladder: CORE ⊂ PLUS ⊂ PRO. Switching a feature ON for a lower
 * plan switches it on for every plan above it, and switching it OFF for a
 * higher plan switches it off below — otherwise a PRO store could end up with
 * fewer features than a PLUS one, silently, with nothing to flag it. Add-ons
 * are independent of the ladder and of each other.
 */
function cellsForToggle(feature, column, next, columns) {
  const value = next ? 1 : 0;

  if (isAddonColumn(column)) return { [column]: value };

  const plans = columns.filter((c) => c.group !== 'addon').map((c) => c.key);
  const index = plans.indexOf(column);
  if (index === -1) return { [column]: value };

  const affected = next ? plans.slice(index) : plans.slice(0, index + 1);

  return affected.reduce((patch, key) => ({ ...patch, [key]: value }), {});
}

/** The closed control: the most specific chip, plus a count of the rest. */
function chipSummary(selected, columns) {
  if (selected.length === 0) return { lead: null, extra: 0 };

  const labelOf = (key) => columns.find((c) => c.key === key)?.label || key;
  // An add-on is the more telling half of "PLUS, PRO, E-Commerce Manager".
  const lead = selected.find(isAddonColumn) || selected[0];

  return { lead: labelOf(lead), extra: selected.length - 1 };
}

function SoldWithControl({ feature, columns, open, onOpen, panelId }) {
  const { lead, extra } = chipSummary(soldColumns(feature, columns), columns);

  const chips = (
    <>
      {lead
        ? <span className="ent-sold__chip">{lead}</span>
        : <span className="ent-sold__chip ent-sold__chip--none">Nobody</span>}
      {extra > 0 && <span className="ent-sold__more">+{extra}</span>}
    </>
  );

  // A quota is set as numbers in the cells, so here the chips report rather
  // than edit. Same box, same chips — there is just nothing to open.
  if (feature.type === 'limit') {
    return <span className="ent-sold ent-sold--static">{chips}</span>;
  }

  return (
    <button
      type="button"
      className={`ent-sold ${open ? 'ent-sold--open' : ''}`}
      aria-expanded={open}
      aria-controls={open ? panelId : undefined}
      aria-label={`Plans and add-ons ${feature.label} is sold with`}
      onClick={onOpen}
    >
      {chips}
      <svg className="ent-sold__caret" width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2.5 4.5L6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/** The open picker: every column as a toggle pill, in its two groups. */
function SoldWithPanel({ feature, columns, onToggle, onClose, id }) {
  const selected = soldColumns(feature, columns);

  const groups = [
    { key: 'plan', label: 'Plans', columns: columns.filter((c) => c.group !== 'addon') },
    { key: 'addon', label: 'Add-ons', columns: columns.filter((c) => c.group === 'addon') },
  ];

  return (
    <div
      className="ent-sold-panel"
      id={id}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      {groups.filter((g) => g.columns.length > 0).map((g) => (
        <div className="ent-sold-panel__group" key={g.key}>
          <span className="ent-sold-panel__label">{g.label}</span>
          <div className="ent-sold-panel__pills">
            {g.columns.map((c) => {
              const on = selected.includes(c.key);

              return (
                <button
                  key={c.key}
                  type="button"
                  className={`ent-pill ${on ? 'ent-pill--on' : ''}`}
                  aria-pressed={on}
                  onClick={() => onToggle(feature, c.key, !on)}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The tag beside a feature name.
 *
 * Future Release and Coming soon only. The "Always on" tag that used to sit
 * on the ten load-bearing features is gone (Alex, 2026-10-26): every row
 * looks the same and behaves the same, so nothing is greyed out or locked.
 *
 * Coming soon (Alex, 2026-10-27) = DAO Watches only, whatever the cells say;
 * the cells record where it will be sold once it launches.
 */
function StatePill({ state }) {
  if (state === 'future') return <span className="ent-future">Future</span>;
  if (state === 'coming_soon') {
    return (
      <span className="ent-future ent-future--soon" title="DAO Watches only until it launches">
        Coming soon
      </span>
    );
  }
  return null;
}

/** The feature name, with the "what is it / where is it" card on hover or focus. */
function FeatureName({ feature }) {
  const guide = FEATURE_GUIDE[feature.key];
  const name = <span className="ent-name">{feature.label}</span>;
  if (!guide) return name;

  const card = (
    <span className="ent-guide">
      <FeatureScreen featureKey={feature.key} label={feature.label} />
      <span className="ent-guide__what">{guide.what}</span>
      {guide.path.length > 0 && (
        <span className="ent-guide__where">Find it: {guide.path.join(' › ')}</span>
      )}
    </span>
  );

  return (
    <Tooltip content={card} side="right" className="ent-guide-tip">
      {/* Focusable so keyboard users get the card too. */}
      <span className="ent-name ent-name--guide" tabIndex={0}>{feature.label}</span>
    </Tooltip>
  );
}

/** The "Review" note: what still needs review or work on this row. */
function ReviewNote({ feature, onNote }) {
  return (
    <textarea
      className="ent-note"
      rows={1}
      value={feature.review_note || ''}
      placeholder="Add note"
      aria-label={`Review notes for ${feature.label}`}
      onChange={(e) => onNote(feature.key, e.target.value)}
      onBlur={(e) => onNote(feature.key, e.target.value, { flush: true })}
    />
  );
}

/** One cell's control, shared by the table and the card layouts. */
function MatrixControl({ feature, column, columnLabel, onCell, onToggle }) {
  if (feature.type === 'limit') {
    // Quotas are a plan property; an add-on never grants one, so no dead input.
    if (isAddonColumn(column)) return <span className="ent-matrix__na" aria-hidden="true" />;

    return (
      <LimitCell
        value={feature.values[column]}
        onChange={(value, opts) => onCell(feature.key, column, value, opts)}
      />
    );
  }

  return (
    <CheckCell
      checked={(feature.values[column] ?? 0) !== 0}
      label={`${feature.label} in ${columnLabel}`}
      onChange={(next) => onToggle(feature, column, next)}
    />
  );
}

function MatrixTable({ grouped, columns, onCell, onToggle, onNote }) {
  const plans = columns.filter((c) => c.group !== 'addon');
  const addons = columns.filter((c) => c.group === 'addon');
  const firstAddon = addons[0]?.key;

  return (
    <div className="ent-matrix">
      <table>
        <thead>
          <tr className="ent-matrix__band">
            <th className="ent-matrix__feature-head" aria-hidden="true" />
            <th className="ent-matrix__review-head" aria-hidden="true" />
            <th colSpan={plans.length}>Plans</th>
            <th colSpan={addons.length} className="ent-matrix__divide">Add-ons</th>
          </tr>
          <tr className="ent-matrix__heads">
            <th className="ent-matrix__feature-head" scope="col">Feature</th>
            <th className="ent-matrix__review-head" scope="col">Review</th>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={c.key === firstAddon ? 'ent-matrix__divide' : ''}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grouped.map(([category, features]) => (
            <React.Fragment key={category}>
              <tr className="ent-matrix__category">
                <td colSpan={columns.length + 2}>{category}</td>
              </tr>
              {features.map((f) => (
                <tr className="ent-matrix__row" key={f.key}>
                  <th scope="row" className="ent-matrix__feature">
                    <FeatureName feature={f} />
                    <StatePill state={f.state} />
                  </th>
                  <td className="ent-matrix__review-cell">
                    <ReviewNote feature={f} onNote={onNote} />
                  </td>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`ent-matrix__cell ${c.key === firstAddon ? 'ent-matrix__divide' : ''}`}
                    >
                      <MatrixControl
                        feature={f}
                        column={c.key}
                        columnLabel={c.label}
                        onCell={onCell}
                        onToggle={onToggle}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Narrow viewports: a card per feature. Nine columns cannot survive a phone,
 * so the selection is the chips plus the open picker, and that is the whole
 * control — there is no second grid of cells to keep in step.
 */
function MatrixCards({ grouped, columns, openKey, onOpenKey, onCell, onToggle, onNote }) {
  const plans = columns.filter((c) => c.group !== 'addon');

  return (
    <div className="ent-cards">
      {grouped.map(([category, features]) => (
        <React.Fragment key={category}>
          <h4 className="ent-cards__category">{category}</h4>
          {features.map((f) => {
            const open = openKey === f.key;
            const panelId = `ent-sold-card-${f.key}`;

            return (
              <div className={`ent-card ${open ? 'ent-card--open' : ''}`} key={f.key}>
                <span className="ent-card__name">
                  <FeatureName feature={f} />
                  <StatePill state={f.state} />
                </span>

                <div className="ent-card__tier">
                  <span className="ent-card__label">Sold with</span>
                  <SoldWithControl
                    feature={f}
                    columns={columns}
                    open={open}
                    panelId={panelId}
                    onOpen={() => onOpenKey(open ? null : f.key)}
                  />
                </div>

                {open && (
                  <SoldWithPanel
                    feature={f}
                    columns={columns}
                    id={panelId}
                    onToggle={onToggle}
                    onClose={() => onOpenKey(null)}
                  />
                )}

                {f.type === 'limit' && (
                  <div className="ent-card__group">
                    <span className="ent-card__label">Limit per plan</span>
                    <div className="ent-card__cells">
                      {plans.map((c) => (
                        <span className="ent-card__cell" key={c.key}>
                          <span className="ent-card__cell-label">{c.label}</span>
                          <MatrixControl
                            feature={f}
                            column={c.key}
                            columnLabel={c.label}
                            onCell={onCell}
                            onToggle={onToggle}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="ent-card__group">
                  <span className="ent-card__label">Review</span>
                  <ReviewNote feature={f} onNote={onNote} />
                </div>
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * A limit cell: a number, or the word Unlimited.
 *
 * -1 is unlimited on the wire. It used to show as an empty box with an ∞
 * placeholder beside a toggle; the toggle was an `rc-btn`, which the legacy
 * settings cascade renders as a white square on the dark grid. Unlimited is
 * now its own pill — there is no number to show, so showing an empty input
 * was only ever an invitation to type into a field that would be ignored.
 */
function LimitCell({ value, onChange }) {
  const unlimited = value === -1;

  if (unlimited) {
    return (
      <div className="ent-limit-cell">
        <button
          type="button"
          className="ent-limit-cell__unlimited"
          aria-pressed="true"
          title="Switch back to a number"
          onClick={() => onChange(0)}
        >
          Unlimited
        </button>
      </div>
    );
  }

  return (
    <div className="ent-limit-cell">
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => {
          const parsed = parseInt(e.target.value, 10);
          onChange(Number.isNaN(parsed) ? 0 : Math.max(0, parsed), { debounce: true });
        }}
        aria-label="Limit"
      />
      <button
        type="button"
        className="ent-limit-cell__infinity"
        aria-pressed="false"
        title="Unlimited"
        onClick={() => onChange(-1)}
      >
        ∞
      </button>
    </div>
  );
}
